import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";
import Marquee from "react-fast-marquee";
import { EVENT, PDF_BG } from "../data/content";

export default function Hero() {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });
    const y = useTransform(scrollYProgress, [0, 1], [0, 200]);
    const opacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);
    const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

    return (
        <section
            id="home"
            ref={ref}
            className="relative min-h-screen w-full overflow-hidden bg-[#0a0a0a] grain-overlay"
            data-testid="hero-section"
        >
            {/* Background image with parallax */}
            <motion.div
                style={{ y, scale }}
                className="absolute inset-0 z-0"
                aria-hidden
            >
                <img
                    src={PDF_BG.hero}
                    alt=""
                    className="w-full h-full object-cover opacity-65"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a]/32 via-[#0a0a0a]/62 to-[#0a0a0a]/94" />
                <div className="absolute inset-0 shadow-[inset_0_140px_130px_rgba(10,10,10,0.62),inset_0_-160px_140px_rgba(10,10,10,0.9)]" />
            </motion.div>

            {/* Glow orbs */}
            <div className="pointer-events-none absolute -top-32 -left-32 w-[480px] h-[480px] rounded-full bg-[#FF6B35]/30 blur-[140px] animate-pulse-glow" />
            <div className="pointer-events-none absolute -bottom-40 -right-32 w-[520px] h-[520px] rounded-full bg-[#9D4CDD]/30 blur-[160px] animate-pulse-glow" />

            <motion.div
                style={{ opacity }}
                className="relative z-10 min-h-screen flex flex-col justify-between max-w-[1440px] mx-auto px-6 md:px-12 pt-28 md:pt-32 pb-12"
            >
                {/* Top bar */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 text-xs uppercase tracking-[0.3em] text-zinc-400 font-mono">
                    <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.6 }}
                    >
                        <span className="text-[#FF6B35]">●</span> {EVENT.poweredBy}
                    </motion.div>
                    <motion.div
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.6 }}
                        className="md:text-right"
                    >
                        India / Jaipur / Rajasthan · Est. 2026
                    </motion.div>
                </div>

                {/* Main title */}
                <div>
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                        className="flex items-center gap-3 text-xs uppercase tracking-[0.3em] text-[#FF6B35] font-mono"
                    >
                        <Sparkles size={14} /> {EVENT.tagline}
                    </motion.div>

                    <motion.h1
                        initial={{ opacity: 0, y: 50 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.45, duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
                        className="mt-6 font-display font-black leading-[0.85] tracking-tighter text-white"
                    >
                        <span className="block text-5xl sm:text-6xl lg:text-[6.5rem]">JAIPUR</span>
                        <span className="block text-5xl sm:text-6xl lg:text-[6.5rem]">
                            AD <span className="gradient-text">FEST</span>
                        </span>
                        <span className="block text-stroke text-5xl sm:text-6xl lg:text-[6.5rem] font-black">
                            2026
                        </span>
                    </motion.h1>

                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.9, duration: 0.8 }}
                        className="mt-8 max-w-xl text-zinc-300 text-base md:text-lg leading-relaxed"
                    >
                        {EVENT.subline} Two days. One city. The minds that shape India's
                        advertising, media & creator economy — all in one room.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 1.05, duration: 0.8 }}
                        className="mt-8 flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-3 sm:gap-4"
                    >
                        <a
                            href="#contact"
                            data-testid="hero-cta-register"
                            className="group inline-flex items-center justify-center gap-3 rounded-full bg-[#FF6B35] hover:bg-[#FF8957] text-black font-bold uppercase tracking-wider text-sm px-6 sm:px-7 py-4 transition-all hover:scale-105"
                        >
                            Register Interest
                            <ArrowRight size={16} className="group-hover:translate-x-1 transition" />
                        </a>
                        <a
                            href="#about"
                            data-testid="hero-cta-explore"
                            className="inline-flex items-center justify-center gap-3 rounded-full glass-light hover:border-[#FF6B35] text-white font-bold uppercase tracking-wider text-sm px-6 sm:px-7 py-4 transition-all"
                        >
                            Explore the Festival
                        </a>
                    </motion.div>
                </div>

                {/* Bottom info */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.2, duration: 0.8 }}
                    className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-5 md:gap-6 pt-10 border-t border-white/10"
                >
                    <div>
                        <div className="text-xs uppercase tracking-[0.25em] text-zinc-500 font-mono">Dates</div>
                        <div className="font-display text-xl md:text-2xl mt-1">{EVENT.daysShort}</div>
                    </div>
                    <div>
                        <div className="text-xs uppercase tracking-[0.25em] text-zinc-500 font-mono">Year</div>
                        <div className="font-display text-xl md:text-2xl mt-1">2026</div>
                    </div>
                    <div>
                        <div className="text-xs uppercase tracking-[0.25em] text-zinc-500 font-mono">Venue</div>
                        <div className="font-display text-xl md:text-2xl mt-1">Angel Resorts</div>
                    </div>
                    <div>
                        <div className="text-xs uppercase tracking-[0.25em] text-zinc-500 font-mono">City</div>
                        <div className="font-display text-xl md:text-2xl mt-1">Jaipur, IN</div>
                    </div>
                </motion.div>
            </motion.div>

            {/* Bottom marquee */}
            <div className="relative z-10 py-6 border-t border-white/10 bg-[#0a0a0a]/70 backdrop-blur-md">
                <Marquee gradient={false} speed={50}>
                    {Array.from({ length: 6 }).map((_, i) => (
                        <span
                            key={i}
                            className="inline-flex items-center gap-6 text-2xl md:text-3xl font-display font-bold uppercase tracking-tight px-6"
                        >
                            <span className="text-white">Create</span>
                            <span className="text-[#FF6B35]">·</span>
                            <span className="text-stroke">Display</span>
                            <span className="text-[#9D4CDD]">·</span>
                            <span className="text-white">Disrupt</span>
                            <span className="text-[#FF6B35]">·</span>
                            <span className="text-stroke">Define</span>
                            <span className="text-[#9D4CDD]">·</span>
                        </span>
                    ))}
                </Marquee>
            </div>
        </section>
    );
}
